import React, { useEffect, useRef, useState } from 'react';
import './Scanner.css';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { setScannerAttendenceList, setOnLeaveDate } from '../../../erpStore/actions';
import { Space, TimePicker } from "antd";
import { toast } from 'react-toastify';
import moment from 'moment';
import { Calendar } from 'primereact/calendar';
import profile from '../../../assets/images/profile.png';
import { Loader } from 'rsuite';

/**
 * Scanner Component
 * Author: Vishal Lohbande
 * Handles the employee attendance scanning system.
 * Features include:
 * - Marking in-time and out-time via physical USB card scanner or manual entry.
 * - Viewing attendance records for a specific date.
 * - Identifying full-day leaves to prevent invalid attendance entries.
 * - Admin and role-based permissions for modifying attendance.
 */
function Scanner() {
  const dispatch = useDispatch();
  const selfInfo = useSelector((state) => state.data.selfData);
  const url = useSelector((state) => state.data.apiUrl);
  const onLeaveDate = useSelector((state) => state.data.onLeaveDate);
  const scannerAttendenceList = useSelector((state) => state.data.scannerAttendenceList);

  const stringHeaders = window.localStorage.getItem('myHeaders');
  const storedHeaders = stringHeaders ? JSON.parse(stringHeaders) : {};

  const [routeDate, setRouteDate] = useState("");
  const [isInTimeRadio, setInTimeRadio] = useState(true);
  const [isInOutRadio, setInOutRadio] = useState(false);
  const [userIdPut, setUserIdPut] = useState("");
  const dateRouteRef = useRef(null);
  const inputStartTimeRef = useRef(null);
  const lastInputTimeRef = useRef(null);
  const [tempAttObj, setTempAttObj] = useState(null);
  const [isDateRoute, setIsDateRoute] = useState(false);

  const [inTimeIndex, setInTimeIndex] = useState(null);
  const [outTimeIndex, setOutTimeIndex] = useState(null);
  const [isInTimeFlag, setIsInTimeFlag] = useState(false);
  const [isOutTimeFlag, setIsOutTimeFlag] = useState(false);

  const [remountFlag, setRemountFlag] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isScanLoad, setIsScanLoad] = useState(false);
  const [isScanIndex, setIsScanIndex] = useState(null);
  const [isRotating, setRotating] = useState(false);

  // ---------- Utilities ---------- //

  /**
   * Parses a formatted date string into 'YYYY-MM-DD' format.
   * @param {string} dateString - The date string (e.g., 'dddd, DD MMM YYYY').
   * @returns {string} Formatted date string.
   */
  const parseDateString = (dateString) => {
    return moment(dateString, 'dddd, DD MMM YYYY').format('YYYY-MM-DD');
  };

  /**
   * Gets the current local time.
   * @returns {string} Current time in 'HH:mm:ss' format.
   */
  const getCurrentTime = () => {
    return moment().format('HH:mm:ss');
  };

  /**
   * Converts 24-hour time string into a 12-hour format with AM/PM.
   * @param {string} timeString - The time string ('HH:mm:ss').
   * @returns {string} Formatted time string ('hh:mm a') or '-'.
   */
  const convertTimeFormat = (timeString) => {
    return timeString ? moment(timeString, 'HH:mm:ss').format('hh:mm a') : '-';
  };

  /**
   * Checks if the currently selected date is before the current day.
   * @returns {boolean} True if the date is in the past, false otherwise.
   */
  const handleFutureDate = () => {
    return moment(onLeaveDate, 'dddd, DD MMM YYYY').isBefore(moment(), 'day');
  };

  /**
   * Validates whether attendance can be updated on the selected date.
   * Prevents updates on past dates.
   * @returns {boolean} True if valid, false otherwise.
   */
  const canUpdateAttendance = () => {
    const formattedDate = parseDateString(onLeaveDate);
    const today = moment().format('YYYY-MM-DD');
    if (formattedDate < today) {
      toast.error("cannot update the attendance", { autoClose: 2000 });
      return false;
    }
    return true;
  };

  /**
   * Checks if an employee is on a full day leave.
   * @param {Object} employee - The employee object from attendance list.
   * @returns {boolean} True if on full day leave, false otherwise.
   */
  const isFullDayLeave = (employee) => {
    if (employee?.leave !== null && employee?.leave?.leaveFormat === 'FULLDAY') {
      toast.error(`${employee?.empFullName} is on full day leave can not update attendance`);
      return true;
    }
    return false;
  };

  /**
   * Requests and verifies if a USB scanner device is connected.
   * @returns {boolean} True if 'USB Reader' is connected, false otherwise.
   */
  const checkConnectedUSBDevices = async () => {
    try {
      const device = await navigator.usb.requestDevice({ filters: [] });
      if (device.productName === 'USB Reader') {
        return true;
      } else {
        setUserIdPut('');
        toast.error('Please connect the Scanner device', { autoClose: 2000 });
        return false;
      }
    } catch (error) {
      console.error('Error accessing USB devices:', error);
      return false;
    }
  };

  // ---------- API Calls ---------- //

  /**
   * Fetches the attendance list for the currently selected date.
   */
  const callScannerAPI = () => {
    setLoader(true);
    dispatch(setScannerAttendenceList([]));
    axios.post(`${url}attendance/for-day`, { "date": parseDateString(onLeaveDate) }, { headers: storedHeaders })
      .then(response => {
        dispatch(setScannerAttendenceList(response.data.attendance));
      })
      .catch(() => { })
      .finally(() => {
        setIsDateRoute(false);
        setLoader(false);
        setRotating(false);
      });
  };

  useEffect(() => {
    if (onLeaveDate) {
      callScannerAPI();
    }
  }, [onLeaveDate]);

  /**
   * Unified API caller to handle PUT requests for attendance updates.
   * Supports both card scan ('inCard', 'outCard') and manual updates ('in', 'out').
   * @param {string|number} identifier - The user ID or card ID.
   * @param {string} type - The type of update ('inCard', 'outCard', 'in', 'out').
   * @param {string} newTime - The new time to be marked.
   * @param {Function} onSuccessCallback - Callback executed on successful update.
   * @param {Function} onFinallyCallback - Callback executed in the finally block.
   */
  const updateAttendanceApiCall = (identifier, type, newTime, onSuccessCallback, onFinallyCallback) => {
    if (!canUpdateAttendance()) {
      if (onFinallyCallback) onFinallyCallback();
      return;
    }

    const isCard = type === 'inCard' || type === 'outCard';
    const isOut = type.toLowerCase().includes('out');
    const timeField = isOut ? 'outTime' : 'inTime';

    const payload = {
      attendanceDate: parseDateString(onLeaveDate),
      [timeField]: newTime
    };

    if (isCard) {
      payload.cardId = identifier;
    } else {
      payload.userId = identifier;
    }

    axios.put(`${url}attendance/update-attendance`, payload, { headers: storedHeaders })
      .then(response => {
        if (response.status === 200) {
          const userIdFromResp = response.data.userId.toString();
          const index = scannerAttendenceList.findIndex(u => u.userId.toString() === userIdFromResp);

          if (index !== -1) {
            const updatedList = [...scannerAttendenceList];
            updatedList[index] = {
              ...updatedList[index],
              [timeField]: response.data[timeField] || newTime
            };
            dispatch(setScannerAttendenceList(updatedList));
          }
          if (onSuccessCallback) onSuccessCallback(response.data);
        }
      })
      .catch(() => { })
      .finally(() => {
        if (onFinallyCallback) onFinallyCallback();
      });
  };

  /**
   * Finds an employee in the current attendance list by User ID.
   */
  const findEmployeeByUserId = (userId) => scannerAttendenceList?.find(emp => emp?.userId?.toString() === userId?.toString());
  
  /**
   * Finds an employee in the current attendance list by Card ID.
   */
  const findEmployeeByCardId = (cardId) => scannerAttendenceList?.find(emp => emp?.cardId?.toString() === cardId?.toString());

  // ---------- Event Handlers ---------- //

  /**
   * Handles keyboard events on the scanner input field.
   * Restricts manual keyboard entry (Enter key) and enforces scanning.
   */
  const handleKeyDawnAttendence = (e) => {
    if (e.key !== 'Enter') return;
    
    const inputValue = e.target.value;
    const employeeByUserId = findEmployeeByUserId(inputValue);
    const employeeByCardId = findEmployeeByCardId(inputValue);

    if (employeeByUserId) {
      toast.error("Manual entry is not allowed. Please scan your card.", { autoClose: 3000 });
    } else if (!employeeByCardId && inputValue.trim() !== '') {
      toast.error("Invalid Card or Employee ID.", { autoClose: 3000 });
    }
    
    setUserIdPut('');
  };

  /**
   * Captures scanner input and updates attendance.
   * Validates typing speed to distinguish between human keystrokes and a physical card scanner.
   */
  const handlePutAttendene = async (e) => {
    const inputValue = e.target.value;
    const now = Date.now();

    if (inputValue.length === 1 || !lastInputTimeRef.current || (now - lastInputTimeRef.current > 1000)) {
      inputStartTimeRef.current = now;
    }
    
    lastInputTimeRef.current = now;
    setUserIdPut(inputValue);

    const employee = findEmployeeByCardId(inputValue);

    if (!employee) {
      // Do not clear the input here because the scanner is likely still typing the rest of the ID characters
      return;
    }

    const timeElapsed = now - inputStartTimeRef.current;
    
    // If it took more than 500ms to input the full card ID, it was typed manually by a human.
    // Scanners operate via fast automated keystrokes (usually < 100ms total).
    if (timeElapsed > 500) {
      toast.error("You cannot type the number manually. Please scan your card.", { autoClose: 3000 });
      setUserIdPut('');
      inputStartTimeRef.current = null;
      lastInputTimeRef.current = null;
      return;
    }

    if (isFullDayLeave(employee)) {
      setUserIdPut('');
      return;
    }

    if (isInTimeRadio) {
      if (employee.inTime === null) {
        if (checkConnectedUSBDevices()) {
          const index = scannerAttendenceList.findIndex(obj => obj?.userId === employee?.userId);
          setIsScanLoad(true);
          setIsScanIndex(index);
          updateAttendanceApiCall(inputValue, 'inCard', getCurrentTime(), 
            () => {
              toast.success(`${employee.empFullName} In Time Marked`, { autoClose: 2000 });
              setUserIdPut('');
            }, 
            () => { setIsScanLoad(false); setUserIdPut(''); }
          );
        }
      } else {
        toast.error(`In Time is already marked for ${employee.empFullName}.`, { autoClose: 3000 });
        setUserIdPut('');
      }
    } else if (isInOutRadio) {
      if (employee.inTime === null) {
        toast.error(`Please add In Time first for ${employee.empFullName}.`, { autoClose: 3000 });
        setUserIdPut('');
        return;
      }
      
      if (employee.outTime === null) {
        if (checkConnectedUSBDevices()) {
          updateAttendanceApiCall(inputValue, 'outCard', getCurrentTime(), 
            () => {
              toast.success(`${employee.empFullName} Out Time Marked`, { autoClose: 2000 });
              setUserIdPut('');
            }, 
            () => setUserIdPut('')
          );
        }
      } else {
        toast.error(`Out Time is already marked for ${employee.empFullName}.`, { autoClose: 3000 });
        setUserIdPut('');
      }
    }
  };

  /**
   * Manual update handler for 'In Time' by an Admin role.
   */
  const handleInTime = (time) => {
    if (!time || !tempAttObj) {
      setIsInTimeFlag(false);
      return;
    }
    const newTime = time.format('HH:mm:ss');
    updateAttendanceApiCall(tempAttObj.userId, 'in', newTime, () => {
      toast.success('In time updated successfully', { autoClose: 2000 });
      setIsInTimeFlag(false);
      setInTimeIndex(null);
    }, () => setIsInTimeFlag(false));
  };

  /**
   * Manual update handler for 'Out Time' by an Admin role.
   */
  const handleOutTime = (time) => {
    if (!time || !tempAttObj) {
      setIsInTimeFlag(false);
      return;
    }
    const newTime = time.format('HH:mm:ss');
    updateAttendanceApiCall(tempAttObj.userId, 'out', newTime, () => {
      toast.success('Out time updated successfully', { autoClose: 2000 });
      setIsInTimeFlag(false);
      setOutTimeIndex(null);
    }, () => setIsInTimeFlag(false));
  };

  /**
   * Manual update handler for 'In Time' by a Manager/Supervisor role.
   */
  const handleInTimeManual = (attObj) => {
    if (isFullDayLeave(attObj)) return;

    if (attObj.inTime === null) {
      updateAttendanceApiCall(attObj.userId, 'in', getCurrentTime(), () => {
        toast.success('In time updated successfully', { autoClose: 2000 });
      });
    } else {
      toast.error('In time already updated', { autoClose: 2000 });
    }
  };

  /**
   * Manual update handler for 'Out Time' by a Manager/Supervisor role.
   */
  const handleOutTimeManual = (attObj) => {
    if (isFullDayLeave(attObj)) return;

    if (attObj.outTime === null) {
      if (attObj.inTime === null) {
        toast.error("Please add the in Time first", { autoClose: 2000 });
        return;
      }
      updateAttendanceApiCall(attObj.userId, 'out', getCurrentTime(), () => {
        toast.success('Out time updated successfully', { autoClose: 2000 });
      });
    } else {
      toast.error('Out time already updated', { autoClose: 2000 });
    }
  };

  /**
   * Changes the selected date to the previous day.
   */
  const handlePrevDate = () => {
    const formatted = moment(onLeaveDate, 'dddd, DD MMM YYYY').subtract(1, 'day').format('dddd, DD MMM YYYY');
    dispatch(setOnLeaveDate(formatted));
    window.sessionStorage.setItem('date', formatted);
  };

  /**
   * Changes the selected date to the next day.
   */
  const handleNextDate = () => {
    const formatted = moment(onLeaveDate, 'dddd, DD MMM YYYY').add(1, 'day').format('dddd, DD MMM YYYY');
    dispatch(setOnLeaveDate(formatted));
    window.sessionStorage.setItem('date', formatted);
  };

  /**
   * Updates the selected date from the calendar router.
   */
  const handleRouteDate = (e) => {
    const formatted = moment(e.target.value).format('dddd, DD MMM YYYY');
    dispatch(setOnLeaveDate(formatted));
  };

  useEffect(() => {
    if (remountFlag && scannerAttendenceList) {
      // Trigger a re-render/dispatch with a new array reference
      dispatch(setScannerAttendenceList([...scannerAttendenceList]));
    }
  }, [remountFlag]);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (dateRouteRef.current && !dateRouteRef.current.contains(event.target)) {
        setIsDateRoute(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const isAdmin = selfInfo?.role?.[0]?.role === 'ROLE_ADMIN' || selfInfo?.role?.[0]?.role === 'ROLE_SUPERADMIN';
  const roleId = selfInfo?.role?.[0]?.roleId;

  return (
    <div className="LeaveCalMainView">
      <div className='leaveManagement-header'>
        <div className='leaveManHeading'>Scanner</div>
      </div>
      <div className='LeaveDayViewMainContainer'>
        <div className='leaveDayViewCalenderHeader'>
          {isAdmin && (
            <div className='dayViewDateSeletor LeavedayViewDateSelector'>
              <svg onClick={handlePrevDate} width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: 'pointer' }}>
                <path d="M5.19231 0L6 0.916667L1.73077 5.5L6 10.0833L5.19231 11L4.76837e-07 5.5L5.19231 0Z" fill="#FF8402" />
              </svg>
              <div className='date-holder-dashboard' onClick={() => setIsDateRoute(true)}>
                <p>{onLeaveDate}</p>
                {isDateRoute && (
                  <div className='calender-holder-dashboard' ref={dateRouteRef}>
                    <Calendar
                      value={moment(routeDate || onLeaveDate, 'dddd, DD MMM YYYY').toDate()}
                      inline
                      onChange={(e) => { handleRouteDate(e); setRouteDate(e.target.value); }}
                      unstyled={false}
                      maxDate={new Date()}
                    />
                  </div>
                )}
              </div>
              {handleFutureDate() && (
                <svg onClick={handleNextDate} width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: 'pointer' }}>
                  <path d="M0.807692 0L0 0.916667L4.26923 5.5L0 10.0833L0.807692 11L6 5.5L0.807692 0Z" fill="#FF8402" />
                </svg>
              )}
            </div>
          )}
          <div className='scannerBtnsField'>
            <button className='scannetIntimeBtn' style={{
              background: isInTimeRadio ? '#FF8402' : 'transparent',
              color: isInTimeRadio ? '#fff' : '#263238',
              border: !isInTimeRadio ? '0.5px solid grey' : 'none'
            }} onClick={() => { setInTimeRadio(true); setInOutRadio(false); }}>In Time</button>
            <button className='scannetOuttimeBtn' style={{
              background: isInOutRadio ? '#FF8402' : 'transparent',
              color: isInOutRadio ? '#fff' : '#263238',
              border: !isInOutRadio ? '0.5px solid grey' : 'none'
            }} onClick={() => { setInTimeRadio(false); setInOutRadio(true); }}>Out Time</button>
            <input
              type='text'
              autoComplete="off"
              style={{ color: isAdmin ? '#263238' : '#FFF' }}
              id="idInput"
              value={userIdPut}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              className='inputFieldForId'
              autoFocus={true}
              placeholder='Employee Id'
              onChange={handlePutAttendene}
              onKeyDown={handleKeyDawnAttendence}
            />
          </div>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" id="refresh-icon"
            className={isRotating ? "rotateRefresh" : ""}
            onClick={() => { setRotating(true); callScannerAPI(); }}>
            <path d="M1 4V10H7" stroke="grey" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M23 20V14H17" stroke="grey" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.49 8.99959C19.9828 7.56637 19.1209 6.28499 17.9845 5.27501C16.8482 4.26502 15.4745 3.55935 13.9917 3.22385C12.5089 2.88834 10.9652 2.93393 9.50481 3.35636C8.04437 3.77879 6.71475 4.5643 5.64 5.63959L1 9.99959M23 13.9996L18.36 18.3596C17.2853 19.4349 15.9556 20.2204 14.4952 20.6428C13.0348 21.0652 11.4911 21.1108 10.0083 20.7753C8.52547 20.4398 7.1518 19.7342 6.01547 18.7242C4.87913 17.7142 4.01717 16.4328 3.51 14.9996" stroke="grey" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div className='leaveDayViewTableView'>
          <div className='leaveTableViewHead'>
            <div className='sr-scanner'>sr</div>
            <div className='empId-scanner'>empId</div>
            <div className='nameMyRow-scanner'>Name</div>
            <div className='InTime-scanner'>In Time</div>
            <div className='outTime-scanner'>Out Time</div>
            <div className='status-scanner'>Status</div>
          </div>
          <div className='leaveDayViewTbody'>
            <div className='leaveDayViewTbodyScroller'>
              {scannerAttendenceList && scannerAttendenceList.length > 0 && scannerAttendenceList.map((attObj, index) => {
                const isFullDay = attObj?.leave?.leaveFormat === 'FULLDAY';
                const rowBackground = attObj?.leave !== null ? (isFullDay ? 'rgb(255, 223, 223)' : 'rgba(98, 63, 173, 0.2)') : 'none';

                return (
                  <div className='myRow' key={attObj.userId || index}>
                    <div className='leaveTableViewHeadTbody' title={attObj?.leave?.leaveReason} style={{ background: rowBackground }}>
                      <div className='sr-scanner'>{index + 1}</div>
                      <div className='empId-scanner'>{attObj.userId}</div>
                      <div className='nameMyRow-scanner'>
                        <div className='holdAttNamehold'>
                          <div className='taskOwnerImage'><img id="taskOwnerImage" src={attObj.empImg || profile} alt='' onError={(e) => { e.target.onerror = null; e.target.src = profile; }} /></div>
                          <div>{attObj.empFullName}</div>
                        </div>
                      </div>

                      {/* In Time Section */}
                      <div className='InTime-scanner' onClick={() => {
                        if (roleId === 1) {
                          setInTimeIndex(index);
                          setIsInTimeFlag(true);
                        } else if (roleId === 2) {
                          handleInTimeManual(attObj);
                          setRemountFlag(!remountFlag);
                        }
                      }}>
                        <div className={isInTimeFlag && inTimeIndex === index ? 'displaiForHIDe' : 'dispalyForOpn'}>
                          {roleId !== 1 && isScanLoad && isScanIndex === index ? <Loader /> : convertTimeFormat(attObj?.inTime)}
                        </div>
                        {roleId === 1 && isInTimeFlag && inTimeIndex === index && (
                          <div className='isDateHolder'>
                            <Space wrap>
                              <TimePicker autoClose className='tempTimePicker' placeholder={attObj?.inTime ? convertTimeFormat(attObj.inTime) : 'In Time'} use24Hours minuteStep={1} secondStep={1} hourStep={1} format={'h:mm a'} onClick={(e) => { e.stopPropagation(); setTempAttObj(attObj); }} onChange={handleInTime} />
                            </Space>
                          </div>
                        )}
                      </div>

                      {/* Out Time Section */}
                      <div className='outTime-scanner' onClick={() => {
                        if (roleId === 1) {
                          setOutTimeIndex(index);
                          setIsOutTimeFlag(true);
                        } else if (roleId === 2) {
                          handleOutTimeManual(attObj);
                          setRemountFlag(!remountFlag);
                        }
                      }}>
                        <div className={isInTimeFlag && outTimeIndex === index ? 'displaiForHIDe' : 'dispalyForOpn'}>
                          {convertTimeFormat(attObj?.outTime)}
                        </div>
                        {roleId === 1 && isOutTimeFlag && outTimeIndex === index && (
                          <div className='isDateHolder'>
                            <Space wrap>
                              <TimePicker className='tempTimePicker' placeholder={attObj?.outTime ? convertTimeFormat(attObj.outTime) : 'Out Time'} use24Hours minuteStep={1} secondStep={1} hourStep={1} format={'h:mm a'} onClick={(e) => { e.stopPropagation(); setTempAttObj(attObj); }} onChange={handleOutTime} />
                            </Space>
                          </div>
                        )}
                      </div>

                      <div className='status-scanner' style={{ color: !attObj.inTime && !attObj.outTime ? 'red' : 'green' }}>
                        {attObj.inTime || attObj.outTime ? 'Present' : 'Absent'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {loader && (!scannerAttendenceList || scannerAttendenceList.length === 0) && (
          <div className="loaderIdProjectsLoad">
            <div className="scannerlist-loader"></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Scanner;